import { useAccount } from '@azure/msal-react';
import { Logout, MenuRounded, PersonOutline } from '@mui/icons-material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { AppBar, Box, Button, Divider, IconButton, Menu, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { type VivaUserScope, vivaUserScopes } from '../../dtos';
import AkaLogo from '../../public/assets/images/haufe/haufe-akademie-logo-icon.svg';
import { BackendConfigContext, sessionStorageApi, sessionStorageKeys } from '../../utils';
import { url } from '../url';
import { AdminScopeSelect } from './AdminScopeSelect';

const getAdminScopesInitially = (): VivaUserScope[] => {
  const scopes = sessionStorageApi.get(sessionStorageKeys.X_VIVA_ADMIN_SCOPES);
  if (scopes === null) {
    return Object.values(vivaUserScopes);
  }

  return Object.values(scopes);
};

export const Header = () => {
  const account = useAccount();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { TaskManagementBoardUrl } = useContext(BackendConfigContext);

  const navigate = useNavigate();

  const [selectedScopes, setSelectedScopes] = useState(getAdminScopesInitially());

  const openMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(menuAnchorEl ? null : event.currentTarget);
  };

  const closeMenu = (): void => {
    setMenuAnchorEl(null);
  };

  // set admin scopes to session storage if correct role
  useEffect(() => {
    if (account?.idTokenClaims?.roles?.includes('Admin')) {
      sessionStorageApi.set(sessionStorageKeys.X_VIVA_ADMIN_SCOPES, selectedScopes);
    } else {
      sessionStorageApi.remove(sessionStorageKeys.X_VIVA_ADMIN_SCOPES);
    }
  }, [selectedScopes, account?.idTokenClaims?.roles]);

  const navigateTo = async (link: string, newTab = false): Promise<void> => {
    closeMenu();
    if (newTab) {
      window.open(link, '_blank');
    } else {
      await navigate(link);
    }
  };

  if (!account) {
    throw new Error('Unauthorized');
  }

  const buttonSx = { textDecoration: 'none', '&:visited:not(:hover)': { color: 'inherit' } };

  return (
    <AppBar
      color="inherit"
      position="sticky"
      sx={(theme) => ({
        zIndex: 5,
        height: '69px',
        border: 'none',
        borderRadius: 0,
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        paddingX: 6,
        paddingY: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      })}
    >
      <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={2}>
          <Box>
            <IconButton onClick={openMenu}>
              <MenuRounded />
            </IconButton>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
              <Stack sx={{ padding: '8px !important' }}>
                <Button variant="menu" component={Link} to={url.root()} onClick={closeMenu} sx={buttonSx}>
                  Dashboard
                </Button>
                <Button variant="menu" component={Link} to={url.fehler()} onClick={closeMenu} sx={buttonSx}>
                  Fehler & Warnungen
                </Button>
                <Button
                  variant="menu"
                  component={Link}
                  to={url.raumRessourcenUebersicht()}
                  onClick={closeMenu}
                  sx={buttonSx}
                >
                  Raum-Ressourcen-Übersicht
                </Button>
                <Divider />
                <Button
                  variant="menu"
                  component="a"
                  href={TaskManagementBoardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                  sx={buttonSx}
                >
                  <OpenInNewOutlinedIcon sx={{ mr: 1, fontSize: '16px' }} />
                  Jira-Board
                </Button>
              </Stack>
            </Menu>
          </Box>
          <IconButton sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2 }} component={Link} to={url.root()}>
            <img alt="Haufe Akademie Logo" src={AkaLogo} style={{ height: '21px' }} />
            <Divider orientation="vertical" sx={{ height: '18px' }} />
            <Typography variant="headingXxs" sx={{ textDecoration: 'none' }} color="primary">
              Viva Planung
            </Typography>
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={4}>
          <AdminScopeSelect selectedScopes={selectedScopes} setSelectedScopes={setSelectedScopes} account={account} />
          <Stack direction="row" alignItems="center">
            <PersonOutline sx={{ mr: '6px' }} />
            <Typography variant="label" sx={{ mr: 2 }}>
              {account.name?.replace(',', '').split(' ').reverse().join(' ')}
            </Typography>
            <IconButton onClick={async () => await navigateTo(url.auth.logout())}>
              <Logout />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </AppBar>
  );
};
